/**
 * Map a site's `projectIntent` to per-type preference prefills.
 *
 * SSOT — Site Project Intent is now the canonical single source of truth
 * for the eight previously-duplicated fields (projectSizeMW,
 * customerCategory, inServiceDate, riskTolerance, powerSourcePreference,
 * coolingPreference, PowerAvailability.powerRequirementMW,
 * WaterResource.coolingType). Those fields no longer live on per-report
 * `*Preferences` interfaces, so this seeder no longer projects them.
 *
 * NWX-4 — per-report-type inference defaults.
 *
 * Each per-type seeder now returns sensible defaults for the
 * report-type-only preference fields, derived from the intent. The
 * dispatcher exposes the same shape to NewReportWizard's
 * `intentDefaults` prop; the preferences form surfaces an "(inferred)"
 * caption next to each inferred-default field and the user can
 * override in one click.
 *
 * Default overwrite protection:
 *   - Defaults apply only to the wizard's first render of a fresh
 *     preferences bag. Once the user (or a sibling/parent report)
 *     populates a field, that value persists and the inference does
 *     not overwrite it. NewReportWizard already implements this by
 *     preferring `existingReport.typedPreferences`, then `sibling`,
 *     then `preferencesFromIntent`. NWX-4 fits cleanly into the
 *     existing chain.
 *
 * Returns Partial<…> so the caller can still merge with the form's
 * existing state.
 *
 * Pack: docs/prompt-packs/New_Report_Wizard_Experience_Prompt.md
 */
import type {
  ProjectIntent,
  FeasibilityPreferences,
  EnvironmentalPreferences,
  TestFitPreferences,
  PowerAvailabilityPreferences,
  WaterResourcePreferences,
  InterconnectionPreferences,
  TypedPreferencesByType,
} from '../types/siteRag';
import type { SiteEntity } from '../types/site';
import type { ReportTypeId } from '../config/reportTypes';
import { effectivePower, bucketMidpointMw } from './projectIntent';

/** Map a bucket literal to its MW basis (midpoint / max / floor). Returns
 *  undefined for the 'undecided' / unknown buckets so callers can treat
 *  "no signal" uniformly. Pure. Thin alias over the shared
 *  `bucketMidpointMw()` SSOT (projectIntent.ts) — kept as a named export so
 *  existing call sites (reportTypePreviewHints, tests) need no churn. */
export function bucketToMidpoint(bucket: ProjectIntent['powerRequirement'] | undefined): number | undefined {
  return bucketMidpointMw(bucket);
}

/** Prefer exact MW from the intent; fall back to the bucket midpoint. */
function mwForIntent(intent: ProjectIntent): number | undefined {
  const eff = effectivePower(intent);
  if (eff.isExact && eff.mw != null) return eff.mw;
  return bucketToMidpoint(intent.powerRequirement);
}

/** Drop undefined keys so an empty bucket doesn't shadow a later user edit. */
function compact<T extends object>(obj: T): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out as Partial<T>;
}

/**
 * NWX-4b helper — default NEPA pathway from intent + site context.
 *
 * Triage logic (US-only today; non-US sites return 'not-applicable'):
 *   - Site lies in the US (no other locale signal present today, so we
 *     default to US) → pick EA / EIS / CatEx by intent.powerRequirement.
 *   - Otherwise → 'not-applicable'.
 *
 * The user can always override in one click. Future work
 * (NWX-FOLLOWUP-LOCALE-DEFAULTS) introduces per-country pathways.
 */
function defaultExpectedNepaPathway(
  intent: ProjectIntent,
  site: SiteEntity | undefined,
): EnvironmentalPreferences['expectedNepaPathway'] {
  // Without an explicit country / federal-nexus signal we treat the site
  // as US (the only locale today's defaults model). Sites whose address
  // suggests non-US would return 'not-applicable' once locale-aware
  // defaults ship.
  const address = (site?.address ?? '').toLowerCase();
  const looksNonUs = /\b(brazil|brasil|mexico|canada|uk|ireland|germany|france|spain|netherlands|sweden|norway|finland|denmark|japan|korea|singapore|australia|india|china)\b/i.test(address);
  if (looksNonUs) return 'unknown'; // strict typing: 'not-applicable' is not in the union
  const mw = mwForIntent(intent);
  if (mw == null) return 'ea';
  if (mw >= 200) return 'eis';
  if (mw < 10) return 'catex';
  return 'ea';
}

// ──────────────────────────────────────────────────────────────────
// NWX-4 — per-report-type inference functions
// ──────────────────────────────────────────────────────────────────

/**
 * NWX-4a — Feasibility.
 *
 * Pre-NWX-4: only carried `kpiWeightOverrides` through from
 * `intent.customWeights`. Now also seeds workload-profile-aware default
 * weights when the user hasn't pinned a custom set. The user's custom
 * weights still win (compact merge preserves them).
 */
export function feasibilityFromIntent(intent: ProjectIntent): Partial<FeasibilityPreferences> {
  const customWeights = intent.customWeights as FeasibilityPreferences['kpiWeightOverrides'] | undefined;
  if (customWeights) {
    return compact({ kpiWeightOverrides: customWeights } satisfies FeasibilityPreferences);
  }
  // Workload-profile-driven defaults (US-biased; NWX-FOLLOWUP-DEFAULTS-FROM-PRIORS
  // will adapt these from prior reports for repeated runs on the same site).
  const workload = intent.workloadProfile;
  let weights: FeasibilityPreferences['kpiWeightOverrides'] | undefined;
  if (workload === 'ai-training') {
    weights = { cooling: 0.30, grid: 0.25, site: 0.20, regulatory: 0.10, logistics: 0.15 };
  } else if (workload === 'hpc') {
    weights = { grid: 0.35, cooling: 0.25, site: 0.15, regulatory: 0.10, logistics: 0.15 };
  } else if (workload === 'ai-inference' || workload === 'enterprise' || workload === 'colocation') {
    weights = { grid: 0.25, cooling: 0.20, site: 0.20, regulatory: 0.20, logistics: 0.15 };
  } else if (workload === 'edge') {
    weights = { grid: 0.20, cooling: 0.15, site: 0.25, regulatory: 0.20, logistics: 0.20 };
  }
  return compact({ kpiWeightOverrides: weights } satisfies FeasibilityPreferences);
}

/**
 * NWX-4b — Environmental.
 *
 * Seeds the existing NEPA and air-basin screening assumptions. A discharge
 * route must be supplied in report setup; rack cooling cannot establish it.
 */
export function environmentalFromIntent(
  intent: ProjectIntent,
  site?: SiteEntity,
): Partial<EnvironmentalPreferences> {
  // Rack-side air/liquid cooling does not identify the heat-rejection sink,
  // blowdown route or discharge permit. Collect an actual discharge decision
  // in this report's setup instead of inferring one from a cooling preference.
  return compact({
    expectedNepaPathway: defaultExpectedNepaPathway(intent, site),
    airBasinOverride: 'auto',
  } satisfies EnvironmentalPreferences);
}

/**
 * NWX-4c — Test Fit.
 *
 * Pre-NWX-4: already inferred `targetFootprintAcres` from the bucket
 * midpoint (3 acres / 10 MW heuristic). Now also seeds `phasing` (single
 * vs phased based on MW) and a default `parkingRatioPerEmployee`.
 */
export function testFitFromIntent(intent: ProjectIntent): Partial<TestFitPreferences> {
  const mw = mwForIntent(intent);
  // The per-phase STATUS picker was removed from the Project Intent form, so
  // every entered phase is now treated as ACTIVE (its presence implies it is
  // in play, not decommissioned). Previously: `.filter(p => p.status !==
  // 'decommissioned')`.
  const activePhases = intent.phases ?? [];
  const phaseCountFromIntent = activePhases.length;
  const phasingFromPhases =
    phaseCountFromIntent > 1 ? 'phased' as const
      : phaseCountFromIntent === 1 ? 'single' as const
        : undefined;
  const phasingFromMw = mw == null ? undefined : (mw > 100 ? 'phased' as const : 'single' as const);
  return compact({
    targetFootprintAcres: mw ? Math.round(mw * 0.3) : undefined,
    phasing: phasingFromPhases ?? phasingFromMw,
    phaseCount: phaseCountFromIntent > 0 ? phaseCountFromIntent : undefined,
    parkingRatioPerEmployee: 1.5,
  } satisfies TestFitPreferences);
}

/**
 * NWX-4d — Power Availability.
 *
 * Nuclear interest does not determine grid connection or non-nuclear BTM.
 * Keep the explicitly selected report strategy authoritative. This helper
 * also seeds renewables preference (from intent.priority), curtailment
 * risk tolerance (from intent.priority), and the substation distance
 * amber/red thresholds (from MW). The `priority` enum is the
 * 'cost' | 'speed' | 'sustainability' | 'resilience' | 'undecided'
 * vocabulary; the renewables preference maps as:
 *   - sustainability → preferred
 *   - resilience     → preferred (BTM-curious projects accept renewables)
 *   - cost           → agnostic
 *   - speed          → unset (speed does not exclude a technology)
 */
export function powerAvailabilityFromIntent(intent: ProjectIntent): Partial<PowerAvailabilityPreferences> {
  const mw = mwForIntent(intent);
  let renewables: PowerAvailabilityPreferences['renewablesPreference'];
  switch (intent.priority) {
    case 'sustainability': renewables = 'preferred'; break;
    case 'resilience':     renewables = 'preferred'; break;
    case 'cost':           renewables = 'agnostic'; break;
    case 'speed':          renewables = undefined; break;
    default:               renewables = undefined; break;
  }
  let curtailment: PowerAvailabilityPreferences['curtailmentRiskTolerance'];
  if (intent.priority === 'speed' || intent.priority === 'resilience') {
    // Speed-to-energise + resilience are time-critical / availability-critical;
    // tolerate the least curtailment risk.
    curtailment = 'low';
  } else if (intent.priority === 'cost') {
    curtailment = 'medium';
  } else if (intent.priority === 'sustainability') {
    curtailment = 'medium';
  } else {
    curtailment = undefined;
  }
  return compact({
    renewablesPreference: renewables,
    curtailmentRiskTolerance: curtailment,
    maxSubstationDistanceMiAmber: mw == null ? undefined : (mw > 100 ? 5 : 3),
    maxSubstationDistanceMiRed: mw == null ? undefined : (mw > 100 ? 10 : 6),
  } satisfies PowerAvailabilityPreferences);
}

/**
 * NWX-4e — Water Resource.
 *
 * Pre-NWX-4: returned `{}`. Now seeds drought scenario severity from
 * MW (larger projects warrant the more conservative 1-in-100) and a
 * default wastewater target from the cooling strategy.
 */
export function waterResourceFromIntent(intent: ProjectIntent): Partial<WaterResourcePreferences> {
  const mw = mwForIntent(intent);
  // Note: rack-side coolingStrategy (air/liquid/hybrid) does not imply a
  // wastewater discharge target (npdes/reuse/potw/zld); wastewaterTarget must
  // remain unset unless explicitly configured by the user.
  return compact({
    droughtScenario: mw == null ? undefined : (mw > 50 ? '1-in-100' : '1-in-10'),
  } satisfies WaterResourcePreferences);
}

/**
 * NWX-4f — Interconnection.
 *
 * Pre-NWX-4: fell through (no entry). Now seeds carrier diversity
 * minimum (always ≥2 for sites that warrant a full report),
 * workload-aware bandwidth target, and IX peering requirement for
 * latency-sensitive workloads.
 */
export function interconnectionFromIntent(intent: ProjectIntent): Partial<InterconnectionPreferences> {
  const workload = intent.workloadProfile;
  const bandwidthGbps =
    workload === 'ai-training' ? 100 :
    workload === 'hpc' ? 40 :
    workload === 'ai-inference' ? 40 :
    workload === 'edge' ? 20 :
    workload === 'enterprise' || workload === 'colocation' ? 40 :
    undefined;
  const ixPeeringRequired =
    workload === 'ai-training' || workload === 'ai-inference' || workload === 'edge'
      ? true
      : undefined;
  return compact({
    carrierDiversityMin: 2,
    bandwidthGbps,
    ixPeeringRequired,
  } satisfies InterconnectionPreferences);
}

/**
 * Dispatch by report type — used by NewReportWizard to seed
 * `initialPreferences`. NWX-4 extends every report type with a
 * sensible default bag derived from the intent; the wizard renders
 * "(inferred — click to override)" captions for fields that came
 * from this seeder rather than the user/sibling/parent.
 *
 * Optional `site` arg lets environmental's NEPA defaulter inspect
 * the site address (country signal). All other report types ignore
 * the site arg today; future locale-aware defaults will read it too.
 */
export function preferencesFromIntent<T extends ReportTypeId>(
  reportType: T,
  intent: ProjectIntent | undefined,
  site?: SiteEntity,
): TypedPreferencesByType[T] | undefined {
  if (!intent) return undefined;
  switch (reportType) {
    case 'feasibility':         return feasibilityFromIntent(intent) as TypedPreferencesByType[T];
    case 'environmental':       return environmentalFromIntent(intent, site) as TypedPreferencesByType[T];
    case 'test-fit':            return testFitFromIntent(intent) as TypedPreferencesByType[T];
    case 'power-availability':  return powerAvailabilityFromIntent(intent) as TypedPreferencesByType[T];
    case 'water-resource':      return waterResourceFromIntent(intent) as TypedPreferencesByType[T];
    case 'interconnection':     return interconnectionFromIntent(intent) as TypedPreferencesByType[T];
    default: return undefined;
  }
}
